import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Play, Pause, RotateCw, Trophy, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { LOTERIA_DECK, shuffleDeck, generateTabla } from '../lib/loteria_deck';
import { gamesApi } from '../lib/api';
import { Button } from '../components/ui/button';

/**
 * Mexican Lotería — pass-and-play.
 * Two tablas (Player 1 + Player 2), the deck acts as auto-caller.
 * A player wins when all 16 cards on their tabla have been called AND
 * they tap their "¡Lotería!" button while their board is fully marked.
 */
export default function GameLoteria() {
    const [tablaP1, setTablaP1] = useState(() => generateTabla());
    const [tablaP2, setTablaP2] = useState(() => generateTabla());
    const [marksP1, setMarksP1] = useState(() => new Set());
    const [marksP2, setMarksP2] = useState(() => new Set());
    const [deck, setDeck] = useState(() => shuffleDeck(LOTERIA_DECK));
    const [deckIdx, setDeckIdx] = useState(-1);
    const [playing, setPlaying] = useState(false);
    const [callSpeedMs] = useState(3500);
    const [winner, setWinner] = useState(null); // 'p1' | 'p2' | null
    const [muted, setMuted] = useState(false);
    const [showSetup, setShowSetup] = useState(true);
    const [playerNames, setPlayerNames] = useState({ p1: 'Player 1', p2: 'Player 2' });
    const timerRef = useRef(null);

    const currentCard = deckIdx >= 0 ? deck[deckIdx] : null;

    // Speak (browser SpeechSynthesis) — optional & mutable.
    const speak = useCallback((text) => {
        if (muted) return;
        try {
            if (!('speechSynthesis' in window)) return;
            const u = new SpeechSynthesisUtterance(text);
            u.lang = 'es-MX';
            u.rate = 0.95;
            window.speechSynthesis.cancel();
            window.speechSynthesis.speak(u);
        } catch {
            // best-effort audio
        }
    }, [muted]);

    // Auto-caller loop
    useEffect(() => {
        if (!playing || winner) return;
        timerRef.current = setTimeout(() => {
            setDeckIdx((idx) => {
                const next = idx + 1;
                if (next >= deck.length) {
                    setPlaying(false);
                    toast.info('Deck exhausted — no winner this round.');
                    return idx;
                }
                const card = deck[next];
                speak(`${card.name}`);
                return next;
            });
        }, callSpeedMs);
        return () => clearTimeout(timerRef.current);
    }, [playing, deckIdx, deck, callSpeedMs, speak, winner]);

    const start = () => {
        setShowSetup(false);
        setPlaying(true);
        // Kick the first call immediately so users don't wait 3.5s in silence
        setDeckIdx(0);
        speak(deck[0].name);
    };

    const pause = () => setPlaying(false);
    const resume = () => setPlaying(true);

    const reset = () => {
        clearTimeout(timerRef.current);
        setTablaP1(generateTabla());
        setTablaP2(generateTabla());
        setMarksP1(new Set());
        setMarksP2(new Set());
        setDeck(shuffleDeck(LOTERIA_DECK));
        setDeckIdx(-1);
        setPlaying(false);
        setWinner(null);
        setShowSetup(true);
    };

    const toggleMark = (player, cardN) => {
        // Only allow marking cards that have actually been called
        const called = deck.slice(0, deckIdx + 1).map(c => c.n);
        if (!called.includes(cardN)) {
            toast.error(`That card hasn't been called yet!`);
            return;
        }
        if (player === 'p1') {
            const next = new Set(marksP1);
            if (next.has(cardN)) next.delete(cardN); else next.add(cardN);
            setMarksP1(next);
        } else {
            const next = new Set(marksP2);
            if (next.has(cardN)) next.delete(cardN); else next.add(cardN);
            setMarksP2(next);
        }
    };

    const shoutLoteria = async (player) => {
        const tabla = player === 'p1' ? tablaP1 : tablaP2;
        const marks = player === 'p1' ? marksP1 : marksP2;
        // Valid win = every card on their tabla is both marked AND actually called
        const called = new Set(deck.slice(0, deckIdx + 1).map(c => c.n));
        const allMarked = tabla.every(c => marks.has(c.n));
        const allValid  = tabla.every(c => called.has(c.n));
        if (!allMarked || !allValid) {
            toast.error('¡Trampa! Not all your cards are called yet.');
            return;
        }
        setPlaying(false);
        setWinner(player);
        speak('¡Lotería!');
        toast.success(`🎉 ${playerNames[player]} wins!`);
        // Record the win for whoever tapped
        try {
            await gamesApi.recordWin('loteria', null, tabla.length);
        } catch {
            // silent — win is still valid locally
        }
    };

    if (showSetup) {
        return <SetupScreen playerNames={playerNames} setPlayerNames={setPlayerNames} onStart={start} />;
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-950 via-red-950 to-slate-950 text-white pb-20" data-testid="loteria-game">
            {/* Top bar */}
            <div className="sticky top-0 z-10 bg-slate-950/80 backdrop-blur border-b border-white/10">
                <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/games" className="flex items-center gap-1 text-sm text-slate-300 hover:text-white" data-testid="loteria-back">
                        <ArrowLeft className="w-4 h-4" /> Games
                    </Link>
                    <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setMuted(m => !m)} data-testid="loteria-mute">
                            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                        </Button>
                        {playing ? (
                            <Button size="sm" variant="outline" onClick={pause} data-testid="loteria-pause">
                                <Pause className="w-4 h-4 mr-1" /> Pause
                            </Button>
                        ) : !winner ? (
                            <Button size="sm" onClick={resume} data-testid="loteria-resume">
                                <Play className="w-4 h-4 mr-1" /> Resume
                            </Button>
                        ) : null}
                        <Button size="sm" variant="outline" onClick={reset} data-testid="loteria-reset">
                            <RotateCw className="w-4 h-4 mr-1" /> New game
                        </Button>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 pt-6">
                {/* Called card */}
                <div className="rounded-3xl bg-gradient-to-br from-amber-500 via-orange-500 to-red-600 p-6 mb-6 text-center shadow-2xl" data-testid="loteria-current-card">
                    {currentCard ? (
                        <>
                            <div className="text-6xl mb-2">{currentCard.icon}</div>
                            <div className="text-2xl font-bold">{currentCard.name}</div>
                            <div className="text-white/80 text-sm">#{currentCard.n} · {currentCard.en}</div>
                            <div className="text-[10px] uppercase tracking-widest text-white/60 mt-2">
                                Card {deckIdx + 1} of {deck.length}
                            </div>
                        </>
                    ) : (
                        <div className="text-white/70">Waiting for first call…</div>
                    )}
                </div>

                {/* Winner banner */}
                {winner && (
                    <div className="rounded-2xl bg-emerald-600/20 border border-emerald-400/40 p-4 mb-6 text-center" data-testid="loteria-winner-banner">
                        <div className="text-4xl mb-1">🏆</div>
                        <div className="font-bold text-lg">¡Lotería! {playerNames[winner]} wins</div>
                        <div className="text-sm text-emerald-200 mt-1">Win counted toward your rewards.</div>
                    </div>
                )}

                {/* Two tablas */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Tabla
                        title={playerNames.p1}
                        tabla={tablaP1}
                        marks={marksP1}
                        onMark={(n) => toggleMark('p1', n)}
                        onShout={() => shoutLoteria('p1')}
                        disabled={!!winner}
                        testIdPrefix="loteria-p1"
                    />
                    <Tabla
                        title={playerNames.p2}
                        tabla={tablaP2}
                        marks={marksP2}
                        onMark={(n) => toggleMark('p2', n)}
                        onShout={() => shoutLoteria('p2')}
                        disabled={!!winner}
                        testIdPrefix="loteria-p2"
                    />
                </div>
            </div>
        </div>
    );
}

function SetupScreen({ playerNames, setPlayerNames, onStart }) {
    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-950 via-red-950 to-slate-950 flex items-center justify-center px-4 pt-20 text-white" data-testid="loteria-setup">
            <div className="max-w-md w-full">
                <Link to="/games" className="text-sm text-slate-300 hover:text-white flex items-center gap-1 mb-4">
                    <ArrowLeft className="w-4 h-4" /> Back
                </Link>
                <div className="rounded-3xl bg-white/5 border border-white/10 p-6 backdrop-blur">
                    <div className="text-5xl mb-3 text-center">🎴</div>
                    <h1 className="text-2xl font-bold text-center mb-1">Mexican Lotería</h1>
                    <p className="text-sm text-slate-300 text-center mb-6">
                        Pass-and-play. One phone, two tablas. Auto-caller reads each card in Spanish.
                    </p>

                    <label className="text-xs text-slate-400 uppercase tracking-widest">Player 1</label>
                    <input
                        type="text"
                        value={playerNames.p1}
                        onChange={(e) => setPlayerNames({ ...playerNames, p1: e.target.value || 'Player 1' })}
                        className="w-full mt-1 mb-4 bg-white/10 rounded-lg px-3 py-2 text-white border border-white/10 focus:outline-none focus:border-white/30"
                        data-testid="loteria-p1-name"
                    />

                    <label className="text-xs text-slate-400 uppercase tracking-widest">Player 2</label>
                    <input
                        type="text"
                        value={playerNames.p2}
                        onChange={(e) => setPlayerNames({ ...playerNames, p2: e.target.value || 'Player 2' })}
                        className="w-full mt-1 mb-6 bg-white/10 rounded-lg px-3 py-2 text-white border border-white/10 focus:outline-none focus:border-white/30"
                        data-testid="loteria-p2-name"
                    />

                    <Button
                        onClick={onStart}
                        className="w-full bg-gradient-to-r from-amber-500 to-red-600 hover:from-amber-600 hover:to-red-700"
                        data-testid="loteria-start-btn"
                    >
                        <Play className="w-4 h-4 mr-2" /> Start round
                    </Button>
                    <p className="text-[10px] text-slate-500 mt-3 text-center">
                        First to mark all 16 cards and tap ¡Lotería! wins.
                    </p>
                </div>
            </div>
        </div>
    );
}

function Tabla({ title, tabla, marks, onMark, onShout, disabled, testIdPrefix }) {
    const allMarked = tabla.every(c => marks.has(c.n));
    return (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid={`${testIdPrefix}-tabla`}>
            <div className="flex items-center justify-between mb-3">
                <div className="font-semibold">{title}</div>
                <div className="text-xs text-slate-400">{marks.size} / 16</div>
            </div>
            <div className="grid grid-cols-4 gap-2">
                {tabla.map((card) => {
                    const marked = marks.has(card.n);
                    return (
                        <button
                            key={card.n}
                            type="button"
                            onClick={() => onMark(card.n)}
                            disabled={disabled}
                            data-testid={`${testIdPrefix}-cell-${card.n}`}
                            className={`aspect-square rounded-lg text-center transition-all relative ${
                                marked
                                    ? 'bg-emerald-500/40 border-2 border-emerald-400 scale-95'
                                    : 'bg-white/10 border border-white/10 hover:bg-white/20'
                            } disabled:opacity-50`}
                        >
                            <div className="text-2xl leading-tight mt-1">{card.icon}</div>
                            <div className="text-[9px] text-white/80 leading-tight px-0.5 truncate">{card.name}</div>
                            {marked && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="w-8 h-8 rounded-full bg-emerald-500 border-2 border-white flex items-center justify-center text-xs font-bold">
                                        ✓
                                    </div>
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
            <Button
                onClick={onShout}
                disabled={disabled || !allMarked}
                className={`w-full mt-4 ${allMarked ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-white/10'}`}
                data-testid={`${testIdPrefix}-shout-btn`}
            >
                <Trophy className="w-4 h-4 mr-2" />
                ¡Lotería!
            </Button>
        </div>
    );
}
